/**
 * The authenticated context (TF-COMPANY-SWITCH, contract
 * docs/design/2026-09-14-company-switch-contract.md). One generation number
 * identifies the session-and-company the screen is bound to. Every request
 * is stamped with the generation it started under and delivers only into the
 * same generation (api/client.ts); every query key is hashed with the
 * generation (main.tsx), so an obsolete answer can never land in a newer
 * screen; the page tree remounts on a new generation (Shell.tsx), so local
 * selections and drafts start clean. The phase drives what the shell shows
 * during a transition and what it refuses (new writes, a second switch).
 */
import type { QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { create } from 'zustand';

export type Phase =
  | { kind: 'idle' }
  /** a dirty editor: the person decides to stay or to discard and switch */
  | { kind: 'confirm'; target: { id: number; name: string } }
  /** writes still in flight; the switch waits for their outcome */
  | { kind: 'finishing'; target: { id: number; name: string } }
  /** a write ended without a known outcome; the person checks before anything else */
  | { kind: 'unknown-write' }
  | { kind: 'switching'; target: { id: number; name: string } }
  /** the switch request failed; still the former company */
  | { kind: 'failed' }
  /** the new company is committed but its settings could not be loaded */
  | { kind: 'load-failed'; company: string }
  /** a switch failed and which company is active could not be verified: not the same as being in the former one */
  | { kind: 'unverified' }
  /** another tab changed the session; this tab must not act on what it shows */
  | { kind: 'session-changed' }
  /** the current membership is no longer valid */
  | { kind: 'access-lost' };

interface ContextState {
  /** bumps on login, on a committed switch and on logout */
  gen: number;
  /** the verified company after a switch; null until GET /tenant confirms one */
  tenantId: number | null;
  /** the company's name as known to the shell, for messages */
  companyName: string | null;
  phase: Phase;
  pendingWrites: number;
  /** writes that ended without an outcome (network failure) in this generation */
  unknownWrites: number;
  /** editors with unsaved work, by id, with a label */
  dirty: Record<string, string>;
  /** an exit inside a page (another week, another shift) waiting on the leave question (leave.tsx) */
  leaving: Leaving | null;
  /** the last announcement for the live region */
  announcement: string | null;
  /** set after a committed switch until the shell has checked the route in the new company */
  destinationCheck: { id: number; name: string; role: string; fromPath: string } | null;
  /** this tab's view of the shared session marker (multi-tab policy) */
  sessionMark: string | null;
}

export const useContextStore = create<ContextState>(() => ({
  gen: 1,
  tenantId: null,
  companyName: null,
  phase: { kind: 'idle' },
  pendingWrites: 0,
  unknownWrites: 0,
  dirty: {},
  leaving: null,
  announcement: null,
  destinationCheck: null,
  sessionMark: readSessionMark(),
}));

const set = useContextStore.setState;
export const ctx = useContextStore.getState;

let qc: QueryClient | null = null;
/** main.tsx hands the query client in, so a session end can drop every cached row. */
export function registerQueryClient(client: QueryClient) {
  qc = client;
}
export const queryClient = () => qc;

/* ── multi-tab session marker ───────────────────────────────────────────── */

const MARK = 'timefly.session';
function readSessionMark(): string | null {
  try {
    return localStorage.getItem(MARK);
  } catch {
    return null;
  }
}
/**
 * Written on every committed session change. Other tabs see it through the
 * storage event and before each request; a tab whose mark differs stops
 * acting on its displayed context until reloaded.
 */
function writeSessionMark(userId: number | null, tenantId: number | null): string {
  const mark = `${userId ?? 0}:${tenantId ?? 0}:${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  try {
    localStorage.setItem(MARK, mark);
  } catch {
    /* private mode: single tab by definition */
  }
  return mark;
}
/**
 * True when another tab changed the session. The credential itself is the
 * evidence: the token cookie is shared, so a tab whose in-memory token no
 * longer matches the cookie is displaying a context it may not act in (the
 * request layer passes both). The `localStorage` marker carries the
 * notification, so the banner appears without waiting for a request.
 */
export function sessionChangedElsewhere(memoryToken: string | null, sharedToken: string | null): boolean {
  if (memoryToken != null && sharedToken !== memoryToken) return true;
  const markMine = ctx().sessionMark;
  const markShared = readSessionMark();
  return markMine != null && markShared != null && markShared !== markMine;
}
export function markSessionChanged() {
  if (ctx().phase.kind !== 'session-changed') set({ phase: { kind: 'session-changed' } });
}
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== MARK) return;
    const markMine = ctx().sessionMark;
    if (markMine != null && e.newValue != null && e.newValue !== markMine) markSessionChanged();
  });
}

/* ── generations ────────────────────────────────────────────────────────── */

/** A new session or company is committed: new generation, clean state, cache dropped. */
export function commitGeneration(userId: number | null, tenantId: number | null, companyName: string | null) {
  qc?.cancelQueries();
  qc?.clear();
  set((s) => ({
    gen: s.gen + 1,
    tenantId,
    companyName,
    pendingWrites: 0,
    unknownWrites: 0,
    dirty: {},
    leaving: null,
    sessionMark: writeSessionMark(userId, tenantId),
  }));
}
/** Logout: the context ends and nothing cached survives. */
export function endGeneration() {
  qc?.cancelQueries();
  qc?.clear();
  set((s) => ({
    gen: s.gen + 1,
    tenantId: null,
    companyName: null,
    phase: { kind: 'idle' },
    pendingWrites: 0,
    unknownWrites: 0,
    dirty: {},
    leaving: null,
    announcement: null,
    destinationCheck: null,
    sessionMark: writeSessionMark(null, null),
  }));
}

/* ── writes in flight ───────────────────────────────────────────────────── */

/**
 * Writes are counted per generation. A write started in a former context that
 * finishes after a switch or a logout must not touch the new context's
 * counters, or the new one would believe work is pending, or settled, that is
 * not its own (TF-SWITCH-RECOVERY).
 */
export function beginWrite(gen: number) {
  set((s) => (s.gen === gen ? { pendingWrites: s.pendingWrites + 1 } : {}));
}
export function endWrite(gen: number, outcomeKnown: boolean) {
  set((s) =>
    s.gen === gen
      ? {
          pendingWrites: Math.max(0, s.pendingWrites - 1),
          unknownWrites: s.unknownWrites + (outcomeKnown ? 0 : 1),
        }
      : {},
  );
}
/** Resolves when no write is in flight any more. */
export function whenWritesSettle(): Promise<void> {
  if (ctx().pendingWrites === 0) return Promise.resolve();
  return new Promise((resolve) => {
    const unsub = useContextStore.subscribe((s) => {
      if (s.pendingWrites === 0) {
        unsub();
        resolve();
      }
    });
  });
}
/** New writes are refused while a transition is being settled. */
export function writesBlocked(): boolean {
  const k = ctx().phase.kind;
  return (
    k === 'finishing' ||
    k === 'switching' ||
    k === 'session-changed' ||
    k === 'access-lost' ||
    k === 'unverified' ||
    k === 'load-failed'
  );
}

/* ── dirty editors ──────────────────────────────────────────────────────── */

export function setDirty(id: string, label: string | null) {
  set((s) => {
    const dirty = { ...s.dirty };
    if (label) dirty[id] = label;
    else delete dirty[id];
    return { dirty };
  });
}
export const hasDirty = () => Object.keys(ctx().dirty).length > 0;

/**
 * An exit inside a page with unsaved work: `label` names where the person was going, `go` takes
 * them there, `stay` undoes anything the exit already started, `discard` drops the page's own
 * editor state. Answered by LeaveGuard (leave.tsx) with the same three answers as leaving the page.
 */
export interface Leaving {
  label: string;
  go: () => void;
  stay?: (() => void) | undefined;
  discard?: (() => void) | undefined;
}
/** Goes straight on when nothing is unsaved; otherwise the shell asks first (issue #72). */
export function askBeforeLeaving(
  label: string,
  go: () => void,
  opts: { stay?: (() => void) | undefined; discard?: (() => void) | undefined } = {},
) {
  if (!hasDirty()) return go();
  set({ leaving: { label, go, stay: opts.stay, discard: opts.discard } });
}
export function setLeaving(leaving: Leaving | null) {
  set({ leaving });
}
/** Forkast og fortsett: the work on screen no longer counts as unsaved until an editor changes again. */
export function forgetDirty() {
  set({ dirty: {} });
}

/* ── phase and announcements ────────────────────────────────────────────── */

export function setPhase(phase: Phase) {
  set({ phase });
}
export function announce(text: string | null) {
  set({ announcement: text });
}
export function setDestinationCheck(v: ContextState['destinationCheck']) {
  set({ destinationCheck: v });
}
export function setCompanyName(name: string | null) {
  set({ companyName: name });
}

/** An editor declares its unsaved work; the shell asks before switching company while any is registered. */
export function useDirty(id: string, isDirty: boolean, label: string) {
  useEffect(() => {
    setDirty(id, isDirty ? label : null);
    return () => setDirty(id, null);
  }, [id, isDirty, label]);
}
